import { QueryResult, useQuery, useSubscription } from '@apollo/client'
import { Location, navigate } from '@reach/router'
import * as React from 'react'
import { useEffect } from 'react'
import { BodyText } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { addMinutes, compareDates, isAfter } from '../../../../common/src/date'
import memoize from '../../../../common/src/memoize'
import {
  FetchInboxQuery,
  FetchTasksPageQuery,
  ThreadMessagesFragment,
  FetchThreadQuery,
  ShowingStatus,
  ConfirmationStatus,
  FetchContactShowingsQueryVariables,
  ShowingUpdatesSubscriptionSubscription,
  ShowingUpdatesSubscriptionSubscriptionVariables,
  FetchContactShowingsQuery,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { fetchContactShowings } from '../../graphql/queries/queries'
import { showingUpdatesSubscription } from '../../graphql/subscriptions'
import { getHash, readHashId } from '../route'
import EventItem from './EventItem'

interface NextShowingsProps {
  task: NonNullable<FetchInboxQuery['thread']>['leasingTask'] | NonNullable<FetchTasksPageQuery['task']>
  thread?: ThreadMessagesFragment
  user: FetchUserContextQuery['self']
  contact: FetchThreadQuery['thread']['contactThem']
}

type NextShowingsImplProps = NextShowingsProps &
  QueryResult<FetchContactShowingsQuery, FetchContactShowingsQueryVariables>

class NextShowingsImpl extends React.PureComponent<NextShowingsImplProps> {
  private handlers = {}

  render() {
    const { task, data, user, contact, loading } = this.props
    // we only want to display showings if they're (a) in the future; or (b) currently happening;
    // or (c) ended within a few minutes ago. In practice, it turns out to be too expensive to fetch
    // the actual showing duration for the task/firm, so just assume the showing ends 45 mins after start
    const showingDurationMins = 45
    const now = new Date()

    if (loading) {
      return <BodyText>Loading showings…</BodyText>
    }
    if (data == null || task.address == null) {
      return null
    }

    const { tzName } = task.address.geolocation
    const { showings } = data.contact

    const upcoming = showings
      .filter(
        s =>
          s.showingStatus !== ShowingStatus.REJECTED &&
          isAfter(addMinutes(s.timeStart, showingDurationMins), now) &&
          s.showingContacts
            .filter(sc => sc.contact.id === contact.id)
            .every(
              sc =>
                sc.confirmationStatus !== ConfirmationStatus.REJECTED &&
                sc.confirmationStatus !== ConfirmationStatus.CANCEL_BY_THEM &&
                sc.confirmationStatus !== ConfirmationStatus.CANCEL_BY_US
            )
      )
      .sort((a, b) => compareDates(a.timeStart, b.timeStart))

    const showCal = user.permissions.canViewCalEventUrls

    return (
      <Location>
        {({ location }) => {
          const selectedId = readHashId('next-showing', location.hash)
          return (
            <div>
              {upcoming.length === 0 && <BodyText>No future showings</BodyText>}
              {upcoming.map(showing => {
                const toggleArg = { showingId: showing.id, currHash: location.hash }
                return (
                  <EventItem
                    key={showing.id}
                    showing={showing}
                    highlightContactId={contact.id}
                    tzName={tzName}
                    taskId={task.id}
                    noMinimize
                    select={showing.id === selectedId}
                    onToggleSelect={memoize(this.handlers, toggleArg, () => () => this.toggleSelectShowing(toggleArg))}
                    showCal={showCal}
                    includeDate={true}
                  />
                )
              })}
            </div>
          )
        }}
      </Location>
    )
  }

  private toggleSelectShowing({ showingId, currHash }: { showingId: number; currHash: string }) {
    const hash = getHash('next-showing', showingId)
    if (currHash === hash) {
      navigate('#', { replace: true }).catch(handleError) // Deselect
    } else {
      navigate(hash, { replace: true }).catch(handleError) // Select
    }
  }
}

export default function NextShowingsSection(props: NextShowingsProps) {
  const { task, contact } = props

  const res = useQuery<FetchContactShowingsQuery, FetchContactShowingsQueryVariables>(fetchContactShowings, {
    variables: { contactId: contact.id, taskId: task.id },
    fetchPolicy: 'network-only',
  })

  const subscriptionRes = useSubscription<
    ShowingUpdatesSubscriptionSubscription,
    ShowingUpdatesSubscriptionSubscriptionVariables
  >(showingUpdatesSubscription, {
    variables: { contactId: contact.id, taskId: task.id },
  })

  // Refetch if a relevant showing is updated
  useEffect(() => {
    const { data } = subscriptionRes
    if (data != null && data.showingUpdates.length > 0) {
      console.log('[next showings] refetching...')
      res.refetch().catch(handleError)
    }
  }, [subscriptionRes.data])

  return <NextShowingsImpl {...props} {...res} />
}
